import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { getLoginUrlWithReturn, startCall } from '@/utils/helpers';
import { CreateCallResponse } from '@/api/types/common.types';
import { CREATE_CALL } from '@/api/mutations/ads.mutations';
import { BACKEND_ERRORS } from '@/constants/backendErrors';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { usePhoneNumbersStore } from '@/store/usePhoneNumbersStore';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { usePlatformSource } from '@/hooks/usePlatformSource';

const DELAY_FOR_LOGGING_CALLS = 3000;

export const useOpenContacts = ({
  shouldOpenBottomSheet = true,
  shouldStartCall = true,
} = {}) => {
  const router = useRouter();
  const customPush = useCustomPush();
  const isAuthor = useIsAuthor();
  const source = usePlatformSource();
  const [isContactsOpen, toggleContacts] = useToggleOpenState();
  const floatingCallButtonTimerFinished = useRef(true);

  const [
    phoneNumbers,
    setPhoneNumbers,
    shownNumbersAdsIds,
    addShownNumbersAdId,
  ] = usePhoneNumbersStore(state => [
    state.phoneNumbers,
    state.setPhoneNumbers,
    state.shownNumbersAdsIds,
    state.addShownNumbersAdId,
  ]);

  const [createCall] = useMutation<CreateCallResponse>(CREATE_CALL, {
    onCompleted: data => {
      setPhoneNumbers(data.createCall.entity);
      if (data.createCall.entity.length === 1 && shouldStartCall) {
        startCall(data.createCall.entity[0]);
      }
      if (data.createCall.entity.length > 1 && shouldOpenBottomSheet) {
        toggleContacts();
      }
    },
    onError: error => {
      if (error.message === BACKEND_ERRORS.NOT_AUTH_TO_DO) {
        customPush(getLoginUrlWithReturn(router.asPath, router.asPath));
      }
    },
  });

  function startFloatingCallButtonTimer() {
    floatingCallButtonTimerFinished.current = false;
    setTimeout(() => {
      floatingCallButtonTimerFinished.current = true;
    }, DELAY_FOR_LOGGING_CALLS);
  }

  function handleCallWithLog({
    adId,
    shopId,
  }: {
    adId?: string;
    shopId?: string;
  }) {
    const variables = {
      adId: adId ?? null,
      shopId: shopId ?? null,
      source,
    };

    createCall({ variables });

    if (adId) {
      addShownNumbersAdId(adId);
    }
  }

  function handleCallWithoutLog() {
    if (!phoneNumbers.length) return;

    if (phoneNumbers.length > 1) {
      toggleContacts();
      return;
    }

    if (shouldStartCall) {
      startCall(phoneNumbers[0]);
    }
  }

  function handleFloatingButtonClick({
    adId,
    shopId,
  }: {
    adId?: string;
    shopId?: string;
  }) {
    if (floatingCallButtonTimerFinished.current) {
      handleCallWithLog({ adId, shopId });
      startFloatingCallButtonTimer();
    } else {
      handleCallWithoutLog();
    }
  }

  function getPhoneNumbers({
    adId,
    shopId,
    isFloatingButtonClicked,
  }: {
    adId?: string;
    shopId?: string;
    isFloatingButtonClicked?: boolean;
  }) {
    const isPhoneNumbersShownForAd = Boolean(
      adId && shownNumbersAdsIds.has(adId)
    );

    if ((adId || shopId) && isFloatingButtonClicked) {
      handleFloatingButtonClick({ adId, shopId });
      return;
    }

    if (adId && !isPhoneNumbersShownForAd && !isAuthor) {
      handleCallWithLog({ adId, shopId });
      return;
    }

    if (shopId && !adId) {
      handleCallWithLog({ shopId });
      return;
    }

    if (isPhoneNumbersShownForAd || shopId) {
      handleCallWithoutLog();
    }
  }

  useEffect(() => {
    return () => {
      setPhoneNumbers([]);
    };
  }, []);

  return { isContactsOpen, toggleContacts, getPhoneNumbers, phoneNumbers };
};
